
/**
 * EPL API Service - Comunicación con EPL Lab Builder Backend
 * Puerto: 3002 | Base URL: /api/epl
 * 
 * Funcionalidades:
 * - CRUD Laboratorios
 * - Gestión de sesiones
 * - Actualización de respuestas
 * - Health checks
 */

// ✅ Usar ruta relativa para que funcione en dev (Vite proxy) y producción (Nginx proxy)
const EPL_BASE_URL = '/api/epl';
import { getSafeToken } from '../../utils/tokenHelper';

const isEplApiDebugEnabled = () => {
  try {
    // Debug opt-in: localStorage.setItem('EPL_API_DEBUG','1')
    return Boolean(import.meta?.env?.DEV) && window?.localStorage?.getItem('EPL_API_DEBUG') === '1';
  } catch {
    return false;
  }
};

class EplApiService {
    /**
     * Eliminar resultado de evaluación
     * @param {string} resultId - ID del resultado
     */
    async deleteResult(resultId) {
      return this.makeRequest(`/admin/results/${resultId}`, {
        method: 'DELETE'
      });
    }
  constructor() {
    this.baseURL = EPL_BASE_URL;
  }

  /**
   * Obtener headers con autenticación JWT del sistema principal
   */
  getAuthHeaders() {
    const token = getSafeToken();
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      // Usar JWT del sistema principal
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Obtener User ID desde el token JWT o contexto de autenticación
   */
  getCurrentUserId() {
    try {
      const token = getSafeToken();
      if (token) {
        // Decodificar JWT para obtener user ID (básico, sin verificación)
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.id || payload.sub;
      }
    } catch (error) {
      console.warn('No se pudo obtener user ID del token JWT:', error);
    }
    return null;
  }

  /**
   * Realizar petición HTTP con manejo de errores
   */
  async makeRequest(endpoint, options = {}) {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const authHeaders = this.getAuthHeaders();
      const config = {
        headers: { ...authHeaders, ...options.headers },
        ...options
      };

      const response = await fetch(url, config);
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        const shouldLog = isEplApiDebugEnabled();
        if (shouldLog) {
          console.error('EPL API Backend Error Response:', {
            endpoint,
            status: response.status,
            statusText: response.statusText,
            body: errorText
          });
        }

        let message = '';
        if (contentType.includes('application/json') && errorText) {
          try {
            const errorJson = JSON.parse(errorText);
            message = errorJson?.message || errorJson?.error || '';
          } catch {
            // Silenciar parse errors por defecto
          }
        }
        if (!message) message = errorText || `HTTP ${response.status}: ${response.statusText}`;

        const err = new Error(message);
        err.status = response.status;
        err.endpoint = endpoint;
        throw err;
      }

      // 204 No Content o respuestas vacías no deben romper el parseo
      if (response.status === 204) return null;
      const bodyText = await response.text().catch(() => '');
      if (!bodyText) return null;
      if (contentType.includes('application/json')) {
        return JSON.parse(bodyText);
      }
      // Fallback: mantener compatibilidad si algún endpoint devuelve texto
      return bodyText;
    } catch (error) {
      if (isEplApiDebugEnabled()) {
        console.error(`EPL API Error [${endpoint}]:`, error);
      }
      throw error;
    }
  }

  // =================== HEALTH CHECKS ===================
  
  /**
   * Verificar salud del servidor EPL
   */
  async checkHealth() {
    return this.makeRequest('/health');
  }

  /**
   * Verificar conexión MongoDB
   */
  async checkDatabase() {
    return this.makeRequest('/health/database');
  }

  // =================== LABORATORY CRUD ===================

  /**
   * Crear nuevo laboratorio
   * @param {Object} labData - Configuración del laboratorio
   */
  async createLab(labData) {
    return this.makeRequest('/labs', {
      method: 'POST',
      body: JSON.stringify(labData)
    });
  }

  /**
   * Obtener laboratorio por ID
   * @param {string} labId - ID del laboratorio
   */
  async getLab(labId) {
    return this.makeRequest(`/labs/${labId}`);
  }

  /**
   * Listar laboratorios con paginación
   * @param {Object} params - Parámetros de consulta (page, limit, etc.)
   */
  async listLabs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/labs?${queryString}` : '/labs';
    return this.makeRequest(endpoint);
  }

  /**
   * Actualizar laboratorio existente
   * @param {string} labId - ID del laboratorio
   * @param {Object} updateData - Datos a actualizar
   */
  async updateLab(labId, updateData) {
    return this.makeRequest(`/labs/${labId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  /**
   * Eliminar laboratorio
   * @param {string} labId - ID del laboratorio
   */
  async deleteLab(labId) {
    return this.makeRequest(`/labs/${labId}`, {
      method: 'DELETE'
    });
  }

  // =================== SESSION MANAGEMENT ===================

  /**
   * Iniciar nueva sesión de laboratorio
   * @param {string} labId - ID del laboratorio
   */
  async startLabSession(labId) {
    return this.makeRequest(`/labs/${labId}/sessions`, {
      method: 'POST',
      body: JSON.stringify({}) // Enviar objeto vacío como sessionData
    });
  }

  /**
   * Actualizar respuestas de sesión
   * @param {string} sessionId - ID de la sesión
   * @param {Object} responses - Respuestas del usuario
   * @param {Object} metadata - Metadatos adicionales (aiAttempts, etc.)
   */
  async updateSessionResponses(sessionId, responses, metadata = {}) {
    return this.makeRequest(`/sessions/${sessionId}/responses`, {
      method: 'PUT',
      body: JSON.stringify({ 
        responses,
        ...metadata // Incluir aiAttempts u otros metadatos
      })
    });
  }

  /**
   * Obtener estado de sesión
   * @param {string} sessionId - ID de la sesión
   */
  async getSessionState(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}`);
  }

  // =================== BUILDER UTILITIES ===================

  /**
   * Validar configuración de laboratorio
   * @param {Object} labConfig - Configuración a validar
   */
  async validateLabConfig(labConfig) {
    // TEMPORALMENTE DESHABILITADO - Endpoint no existe en backend
    // return this.makeRequest('/validate', {
    //   method: 'POST',
    //   body: JSON.stringify(labConfig)
    // });
    
    // Mock response para completar testing E2E
    return {
      success: true,
      isValid: true,
      errors: [],
      warnings: []
    };
  }

  /**
   * Obtener plantillas de laboratorio disponibles
   */
  async getLabTemplates() {
    return this.makeRequest('/templates');
  }

  /**
   * Obtener estadísticas de laboratorio
   * @param {string} labId - ID del laboratorio
   */
  async getLabStats(labId) {
    return this.makeRequest(`/labs/${labId}/stats`);
  }

  // =================== IA EVALUATION ===================

  /**
   * Evaluar respuestas con IA
   * @param {string} labId - ID del laboratorio
   * @param {string} stateId - ID del estado de sesión
   * @param {Object} responses - Respuestas del estudiante
   */
  async evaluateWithAI(labId, stateId, responses) {
    return this.makeRequest(`/labs/${labId}/evaluate`, {
      method: 'POST',
      body: JSON.stringify({ stateId, responses })
    });
  }

  // =================== ADMIN ENDPOINTS ===================

  /**
   * Obtener lista de usuarios con estadísticas EPL
   * @param {Object} filters - Filtros (role, status, search)
   */
  async getAdminUsers(filters = {}) {
    const params = new URLSearchParams();
    if (filters.role && filters.role !== 'all') params.append('role', filters.role);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    
    const queryString = params.toString();
    return this.makeRequest(`/admin/users${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Actualizar sistemas EPL asignados a un usuario
   * @param {string} userId - ID del usuario
   * @param {Array} systems - Array de sistemas ('EPL-Leader Lab', 'EPL-Innova System', 'EPL-Decision System')
   */
  async updateUserSystems(userId, systems) {
    return this.makeRequest(`/admin/users/${userId}/systems`, {
      method: 'PUT',
      body: JSON.stringify({ systems })
    });
  }

  /**
   * Obtener estadísticas detalladas de un usuario
   * @param {string} userId - ID del usuario
   */
  async getUserStats(userId) {
    return this.makeRequest(`/admin/users/${userId}/stats`);
  }

  /**
   * Eliminar un usuario
   * @param {string} userId - ID del usuario
   */
  async deleteUser(userId) {
    return this.makeRequest(`/admin/users/${userId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Actualizar información de un usuario
   * @param {string} userId - ID del usuario
   * @param {Object} userData - { nombre, email, role, status }
   */
  async updateUser(userId, userData) {
    return this.makeRequest(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  /**
   * Crear un nuevo usuario
   * @param {Object} userData - { nombre, email, password, role, eplSystemsAssigned }
   */
  async createUser(userData) {
    return this.makeRequest('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  /**
   * Resetear contraseña de un usuario
   * @param {string} userId - ID del usuario
   * @param {string} newPassword - Nueva contraseña
   */
  async resetUserPassword(userId, newPassword) {
    return this.makeRequest(`/admin/users/${userId}/reset-password`, {
      method: 'PATCH',
      body: JSON.stringify({ newPassword })
    });
  }

  /**
   * Obtener analytics de IA (métricas OpenAI)
   * @param {Object} dateRange - { startDate, endDate } opcional
   */
  async getAIAnalytics(dateRange = {}) {
    const params = new URLSearchParams();
    if (dateRange.startDate) params.append('startDate', dateRange.startDate);
    if (dateRange.endDate) params.append('endDate', dateRange.endDate);
    
    const queryString = params.toString();
    return this.makeRequest(`/admin/ai-analytics${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Obtener logs de evaluaciones IA
   * @param {Object} params - { limit, page }
   */
  async getAILogs(params = { limit: 50, page: 1 }) {
    const queryParams = new URLSearchParams(params);
    return this.makeRequest(`/admin/ai-analytics/logs?${queryParams.toString()}`);
  }

  /**
   * Obtener resultados de evaluaciones
   * @param {Object} filters - { search, labFilter, userFilter }
   */
  async getResults(filters = {}) {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.labFilter) params.append('labFilter', filters.labFilter);
    if (filters.userFilter) params.append('userFilter', filters.userFilter);
    
    const queryString = params.toString();
    return this.makeRequest(`/admin/results${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Exportar resultados en CSV
   * @param {Object} filters - { search, labFilter, userFilter }
   */
  async exportResultsCSV(filters = {}) {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.labFilter) params.append('labFilter', filters.labFilter);
    if (filters.userFilter) params.append('userFilter', filters.userFilter);
    
    const queryString = params.toString();
    const url = `${this.baseURL}/admin/results/export${queryString ? `?${queryString}` : ''}`;
    
    // Descargar CSV
    const response = await fetch(url, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Error exportando CSV: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `epl-results-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(downloadUrl);
    
    return { success: true };
  }

  // =================== TEACHER FEEDBACK ===================

  /**
   * Enviar feedback docente a estudiante
   * @param {Object} feedbackData - Datos del feedback
   */
  async sendTeacherFeedback(feedbackData) {
    return this.makeRequest('/teacher-messages/send', {
      method: 'POST',
      body: JSON.stringify(feedbackData)
    });
  }

  /**
   * Obtener mensajes del estudiante autenticado
   */
  async getMyMessages() {
    return this.makeRequest('/teacher-messages/my-messages');
  }

  /**
   * Obtener contador de mensajes no leídos
   */
  async getUnreadMessagesCount() {
    try {
      return await this.makeRequest('/teacher-messages/unread-count');
    } catch (_e) {
      return { count: 0 };
    }
  }

  /**
   * Marcar mensaje como leído
   * @param {string} messageId - ID del mensaje
   */
  async markMessageAsRead(messageId) {
    return this.makeRequest(`/teacher-messages/${messageId}/read`, {
      method: 'PUT'
    });
  }

  /**
   * Obtener mensajes enviados por el docente autenticado
   */
  async getSentMessages() {
    return this.makeRequest('/teacher-messages/sent');
  }

  // =================== DEBUG ENDPOINTS (ADMIN ONLY) ===================

  /**
   * Obtener datos RAW de evaluación IA (solo davidprudencio@hotmail.com)
   * @param {string} stateId - ID de la sesión
   */
  async getDebugRawData(stateId) {
    return this.makeRequest(`/debug/lab/${stateId}/raw`);
  }
}

// Instancia singleton para uso global
export const eplApi = new EplApiService();

// Exportar también la clase para testing
export { EplApiService };

// Utilidades adicionales
export const EPL_ENDPOINTS = {
  HEALTH: '/health',
  LABS: '/labs',
  SESSIONS: '/sessions',
  VALIDATE: '/validate',
  TEMPLATES: '/templates',
  ADMIN_USERS: '/admin/users',
  ADMIN_AI_ANALYTICS: '/admin/ai-analytics',
  DEBUG_RAW: '/debug/lab'
};

export default eplApi;